/**
 * SEO 메타 관리 (CSR 환경)
 *
 * - 라우트 전환마다 title / description / robots / canonical / Open Graph / Twitter 메타를 갱신한다.
 * - 상세 페이지(브랜드·이벤트·공지)는 데이터 로드 후 setPageMeta()로 덮어쓴다.
 * - canonical 은 현재 호스트 기준 https 절대 경로(쿼리 제외)로 만든다. 은행별 서브도메인(kbprm, nhprm 등)은
 *   각자 자기 호스트를 canonical 로 가지므로 서로 중복 콘텐츠로 묶이지 않는다.
 */

import { MAIN_ORIGIN, isHostSpecificPath, profileForHost } from "@/lib/siteProfiles";

export const SITE_NAME = "하이비타민";
export const DEFAULT_TITLE = "하이비타민 | 복지카드 제휴 가맹점 청구할인";
export const DEFAULT_DESCRIPTION =
	"복지카드 및 기업 패밀리카드로 제휴 가맹점에서 결제하면 최대 20% 청구할인. 추가 가입·연회비·전월 실적 없이 기존 할인과 중복 적용. 외식·쇼핑·호텔·키즈 등 200여 개 제휴 브랜드와 매장 안내.";
export const DEFAULT_OG_IMAGE_PATH = "/og-image-hivitamin.png";

const TITLE_SUFFIX = " | " + SITE_NAME;
const MAX_DESCRIPTION_LENGTH = 160;

function ensureMeta(attr, key) {
	let element = document.head.querySelector(`meta[${attr}="${key}"]`);
	if (!element) {
		element = document.createElement("meta");
		element.setAttribute(attr, key);
		document.head.appendChild(element);
	}
	return element;
}

function setNamedMeta(name, content) {
	if (content == null || content === "") {
		const existing = document.head.querySelector(`meta[name="${name}"]`);
		if (existing) existing.remove();
		return;
	}
	ensureMeta("name", name).setAttribute("content", content);
}

function setPropertyMeta(property, content) {
	if (content == null || content === "") return;
	ensureMeta("property", property).setAttribute("content", content);
}

function ensureLink(rel) {
	let element = document.head.querySelector(`link[rel="${rel}"]`);
	if (!element) {
		element = document.createElement("link");
		element.setAttribute("rel", rel);
		document.head.appendChild(element);
	}
	return element;
}

export function siteOrigin() {
	if (typeof window === "undefined") return "";
	// 프리렌더 중에는 로컬 정적 서버가 아니라 운영 도메인을 canonical 로 쓴다.
	const injected = window.__PRERENDER_INJECTED;
	if (injected && injected.siteUrl) return injected.siteUrl.replace(/\/+$/, "");
	// 운영은 항상 https. 로컬(http://localhost)만 그대로 둔다.
	const host = window.location.host;
	const isLocal = /^(localhost|127\.0\.0\.1)(:\d+)?$/.test(host);
	return (isLocal ? window.location.protocol : "https:") + "//" + host;
}

/** 현재 사이트 호스트명(소문자). 프리렌더 중에는 주입된 운영 호스트를 돌려준다. 호스트별 분기(kbprm, nhprm)에 쓴다. */
export function siteHostname() {
	if (typeof window === "undefined") return "";
	const injected = window.__PRERENDER_INJECTED;
	if (injected && injected.siteUrl) {
		try {
			return new URL(injected.siteUrl).hostname.toLowerCase();
		} catch (error) {
			// fall through
		}
	}
	return window.location.hostname.toLowerCase();
}

/** 현재 호스트의 카드 프로필 (src/lib/siteProfiles.js). */
export function currentProfile() {
	return profileForHost(siteHostname());
}

/** 설정 API(siteUrl 파라미터)에 넘길 현재 페이지 URL. 프리렌더 중에는 로컬 주소 대신 운영 도메인 기준으로 만든다. */
export function currentSiteUrl() {
	if (typeof window === "undefined") return "";
	return siteOrigin() + window.location.pathname + window.location.search + window.location.hash;
}

export function absoluteUrl(path) {
	if (!path) return siteOrigin() + "/";
	if (/^https?:\/\//i.test(path)) return path;
	return siteOrigin() + (path.startsWith("/") ? path : "/" + path);
}

export function stripHtml(html) {
	if (!html) return "";
	return String(html)
		.replace(/<style[\s\S]*?<\/style>/gi, " ")
		.replace(/<script[\s\S]*?<\/script>/gi, " ")
		.replace(/<[^>]+>/g, " ")
		.replace(/&nbsp;/gi, " ")
		.replace(/&amp;/gi, "&")
		.replace(/&lt;/gi, "<")
		.replace(/&gt;/gi, ">")
		.replace(/&quot;/gi, '"')
		.replace(/\s+/g, " ")
		.trim();
}

export function truncate(text, max = MAX_DESCRIPTION_LENGTH) {
	const clean = stripHtml(text);
	if (clean.length <= max) return clean;
	return clean.slice(0, max - 1).trimEnd() + "…";
}

export function imageUrl(path) {
	if (!path) return absoluteUrl(DEFAULT_OG_IMAGE_PATH);
	if (/^https?:\/\//i.test(path)) return path;
	const base = (process.env.VUE_APP_IMAGE_BASE_URL || siteOrigin() + "/").replace(/\/?$/, "/");
	return base + "images/" + path.replace(/^\/+/, "");
}

/**
 * @param {Object} meta
 * @param {string} [meta.title]        페이지 제목(사이트명 자동 접미). 비우면 기본 제목.
 * @param {string} [meta.fullTitle]    접미 없이 그대로 쓸 제목.
 * @param {string} [meta.description]  160자 내로 자동 정리.
 * @param {string} [meta.partnerDescription] 파트너 호스트(카드별 페이지)에서 description 대신 쓸 문구. {card}→카드명, {intro}→카드 안내 문장.
 * @param {string} [meta.image]        OG 이미지 절대 URL 또는 이미지 서버 상대 경로.
 * @param {string} [meta.path]         canonical 경로. 비우면 현재 pathname.
 * @param {boolean} [meta.noindex]     true 면 robots noindex,nofollow.
 * @param {string} [meta.type]         og:type (기본 website, 상세는 article).
 * @param {string} [meta.origin]       canonical 호스트 강제(예: KB 전용 경로는 https://kbprm.hivitamin.co.kr). 비우면 현재 호스트.
 */
export function setPageMeta(meta = {}) {
	if (typeof document === "undefined") return;
	const profile = currentProfile();
	const path = meta.path || window.location.pathname;
	const canonicalPath = path === "/index.html" ? "/" : path;
	const hostSpecific = isHostSpecificPath(canonicalPath);
	let origin = meta.origin;
	let noindex = Boolean(meta.noindex);
	let rawTitle = meta.title;
	let rawDescription = meta.description;
	if (profile.noindexAll) {
		// 별도 진입점이 아닌 호스트: 전부 bridgem 으로 모은다.
		origin = origin || MAIN_ORIGIN;
		noindex = true;
	} else if (!profile.isMain) {
		if (hostSpecific) {
			// 카드별 페이지: 제목·설명에 카드명을 넣어 다른 호스트와 다른 페이지로 보이게 한다.
			if (!rawTitle) {
				rawTitle = profile.cardName + " 제휴 가맹점 청구할인";
			} else if (!rawTitle.includes(profile.shortName)) {
				rawTitle = profile.cardName + " " + rawTitle.replace(/복지카드 /, ""); // "온라인 쇼핑 복지카드 청구할인" 처럼 중간의 복지카드도 카드명으로 대체
			}
			// description 도 카드명이 앞에 오게 한다. 뒤에 붙이면 공통 문장 100자 뒤로 밀려 160자 절단에 잘리고,
			// 호스트끼리 앞부분이 같아 중복 콘텐츠로 묶인다.
			if (rawDescription && rawDescription.includes(profile.shortName)) {
				// 상세 페이지처럼 이미 카드명이 들어간 설명은 그대로 둔다.
			} else if (meta.partnerDescription) {
				rawDescription = meta.partnerDescription.split("{card}").join(profile.cardName).split("{intro}").join(profile.intro);
			} else {
				rawDescription = profile.intro + (rawDescription ? " " + rawDescription : "");
			}
		} else if (!origin) {
			// 어느 호스트에서나 같은 페이지: 색인은 bridgem 하나로 모은다.
			origin = MAIN_ORIGIN;
			noindex = true;
		}
	}
	const title = meta.fullTitle || (rawTitle ? rawTitle + TITLE_SUFFIX : DEFAULT_TITLE);
	const description = truncate(rawDescription || DEFAULT_DESCRIPTION);
	const canonical = origin
		? origin.replace(/[/]+$/, "") + (canonicalPath.startsWith("/") ? canonicalPath : "/" + canonicalPath)
		: absoluteUrl(canonicalPath);
	const image = imageUrl(meta.image);

	document.title = title;
	setNamedMeta("description", description);
	setNamedMeta("robots", noindex ? "noindex" : null); // follow 는 유지: 공통 페이지에서 bridgem 으로 가는 링크 가중치를 끊지 않는다
	ensureLink("canonical").setAttribute("href", canonical);

	setPropertyMeta("og:site_name", SITE_NAME);
	setPropertyMeta("og:type", meta.type || "website");
	setPropertyMeta("og:title", title);
	setPropertyMeta("og:description", description);
	setPropertyMeta("og:url", canonical);
	setPropertyMeta("og:image", image);
	setPropertyMeta("og:locale", "ko_KR");

	setNamedMeta("twitter:card", "summary_large_image");
	setNamedMeta("twitter:title", title);
	setNamedMeta("twitter:description", description);
	setNamedMeta("twitter:image", image);
}

/** 데이터가 없는 상세 페이지(없는 브랜드 ID 등): 소프트 404 가 색인되지 않도록 noindex 처리한다. */
export function setNotFoundMeta(label) {
	setPageMeta({ fullTitle: (label || "페이지") + "를 찾을 수 없습니다 | " + SITE_NAME, description: "요청하신 " + (label || "페이지") + " 정보가 없습니다.", noindex: true });
}

/** 라우트 meta.seo 기준으로 기본 메타를 적용한다. 상세 페이지는 이후 setPageMeta 로 덮어쓴다. */
export function applyRouteMeta(route) {
	const seo = (route && route.meta && route.meta.seo) || {};
	// 이전 페이지가 넣은 페이지 전용 JSON-LD(FAQ 등)는 제거한다. index.html 의 정적 JSON-LD 는 id 가 없어 유지된다.
	document.querySelectorAll('script[id^="jsonld-"]').forEach((element) => element.remove());
	setPageMeta({
		title: seo.title,
		description: seo.description,
		partnerDescription: seo.partnerDescription,
		noindex: Boolean(seo.noindex),
		path: route && route.path,
		origin: seo.canonicalOrigin,
	});
}

/** JSON-LD 를 <head> 에 넣거나 교체한다. id 로 구분하므로 페이지 전환 시 덮어써진다. */
export function setJsonLd(id, data) {
	if (typeof document === "undefined") return;
	const scriptId = "jsonld-" + id;
	let element = document.getElementById(scriptId);
	if (!data) {
		if (element) element.remove();
		return;
	}
	if (!element) {
		element = document.createElement("script");
		element.type = "application/ld+json";
		element.id = scriptId;
		document.head.appendChild(element);
	}
	element.textContent = JSON.stringify(data);
}
